import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import Layout from "../../components/layout/Layout";
import Panel from "../../components/brand/Panel";
import { useBreadcrumb } from "../../context/BreadcrumbContext";
import { getUnitStatement } from "../../api/financeApi";
import { formatDateUk } from "../../utils/dateDisplay";

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

export default function UnitStatementPage() {
  const { unitId } = useParams();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statement, setStatement] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await getUnitStatement(unitId, params);
      setStatement(res.data);
      setCustomPath([
        { label: "Finance", path: "/finance" },
        { label: res.data.unit_name || `Unit ${unitId}`, path: null },
      ]);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load statement.");
    } finally {
      setLoading(false);
    }
  }, [unitId, dateFrom, dateTo, setCustomPath]);

  useEffect(() => () => clearCustomPath(), [clearCustomPath]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Layout>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        Owner statement
      </Typography>
      {statement && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {statement.unit_name} · {(statement.owner_names?.length
            ? statement.owner_names.join(", ")
            : statement.owner_name) || "Owner not set"}
        </Typography>
      )}

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          label="From"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Panel title="Account activity" noBodyPadding={false}>
        {loading ? (
          <Skeleton height={240} />
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Opening {money(statement?.opening_balance)} · Closing{" "}
              {money(statement?.closing_balance)}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Charge</TableCell>
                    <TableCell align="right">Payment</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(statement?.lines || []).map((line, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{formatDateUk(line.date)}</TableCell>
                      <TableCell>{line.type}</TableCell>
                      <TableCell>
                        {line.invoice_number
                          ? `${line.invoice_number} — ${line.description}`
                          : line.description}
                      </TableCell>
                      <TableCell align="right">{line.charge ? money(line.charge) : "—"}</TableCell>
                      <TableCell align="right">
                        {line.payment ? money(line.payment) : "—"}
                      </TableCell>
                      <TableCell align="right">{money(line.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Panel>
    </Layout>
  );
}
